<template>
  <div class="mobile-button-container">
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>Attendee list</span>
      </v-flex>
      <div class="filtering-header">
        <v-select
          :items="performances"
          item-text="from"
          item-value="id"
          label="Ticket types"
          box
          @change="handlePerformanceChange"
        />
        <v-text-field
          label="Search Attendees"
          append-icon="search"
          box
          @change="handleSearchChange($event)"
        />
      </div>
      <div v-if="!$apollo.loading" class="attendee_table">
        <div
          v-if="attendees.length === 0"
          class="no-attendees-message-container border__left border__right"
        >
          <span class="no-attendees-message">No matching records found.</span>
        </div>
        <table-list
          v-else
          :hide-checkbox="true"
          :event-id="null"
          :organization-slug="organizationSlug"
          :selected.sync="selected"
          :attendees="attendees"
          body-style="grid-auto-columns: 50px minmax(150px, 300px) !important;"
          :visible-fields="visibleFields"
          :pagination="pagination"
          :attendee-fields="attendeeFields"
          @sort="handleSort($event)"
        />
        <table-pagination
          :pagination="{
            ...pagination,
            per_page: pagination.per_page === -1 ? 'All' : pagination.per_page,
          }"
          :total-attendees="totalAttendeeCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </div>
    </v-layout>
  </div>
</template>

<script>
import TableList from './attendee-table/list.vue'
import TablePagination from './attendee-table/pagination.vue'
import moment from 'moment/moment'
import { GET_EVENTS_ORDERS_AND_ORDER_ITEMS_BY_EVENT_TEMPLATE_ID } from '@/graphql/event.graphql'
import { UserType } from '@/types'

const TicketIdType = Object.freeze({
  ALL: 'ALL',
})

export default {
  name: 'theater-accreditations-list-container',
  components: {
    TableList,
    TablePagination,
  },
  props: {
    eventTemplateId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalAttendeeCount: 0,
      performances: [],
      accreditations: [],
      TicketIdType,
      visibleFields: ['Attendee', 'Email', 'Tickets type (No.)', 'No. of tickets', 'Created At'],
      attendeeFields: {},
      selected: [],
      pagination,
      eventId: undefined,
      searchTerm: undefined,
    }
  },
  apollo: {
    getEventsOrdersAndOrderItemsByEventTemplateId: {
      query: GET_EVENTS_ORDERS_AND_ORDER_ITEMS_BY_EVENT_TEMPLATE_ID,
      variables() {
        return {
          eventTemplateId: this.eventTemplateId,
          pagination: this.pagination,
          eventId: this.eventId,
          searchTerm: this.searchTerm,
        }
      },
      manual: true,
      async result({ data, loading }) {
        if (!loading && data) {
          this.performances = [
            { from: 'All performances', id: this.TicketIdType.ALL },
            ...data.getEventsOrdersAndOrderItemsByEventTemplateId.events.map(event => {
              return { ...event, from: moment(event.from).format('DD.MM.YYYY HH:mm') }
            }),
          ]
          this.calculateAndSetAttendeesData(
            data.getEventsOrdersAndOrderItemsByEventTemplateId.orders,
          )
          this.totalAttendeeCount = data.getEventsOrdersAndOrderItemsByEventTemplateId.count
        }
      },
    },
  },
  methods: {
    calculateAndSetAttendeesData(orders) {
      this.attendees = orders.map(order => {
        const {firstName, lastName, email} = this.findBuyerInfo(order)

        return {
          id: order.id,
          name: `${firstName} ${lastName}`,
          email,
          ticketTypes: this.constructTicketTypesString(order.purchases),
          ticketNumber: order.purchases.length,
          createdAt: order.createdAt,
        }
      })
    },
    constructTicketTypesString(purchases) {
      const ticketTypes = new Map()
      for (const purchase of purchases) {
        if (ticketTypes.has(purchase.orderItem.name)) {
          ticketTypes.set(purchase.orderItem.name, ticketTypes.get(purchase.orderItem.name) + 1)
        } else {
          ticketTypes.set(purchase.orderItem.name, 1)
        }
      }
      let typesString = ''
      ticketTypes.forEach((values, keys) => {
        typesString += (typesString ? ' ' : '') + `${keys} (${values});`
      })

      return typesString
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalAttendeeCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)
    },
    handlePerformanceChange(id) {
      if (id === TicketIdType.ALL) this.eventId = undefined
      else this.eventId = id
    },
    handleSearchChange(input) {
      this.searchTerm = input
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 25,
          sortBy: sortBy,
          descending: descending === '1' || descending === 'true',
        },
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    findBuyerInfo(order) {
      if (order.user) {
        return {
          firstName: order.user.firstName,
          lastName: order.user.lastName,
          email: order.user.email,
        }
      } else if (order.buyer && order.buyer.fields) {
        return {
          firstName: order.buyer.fields.firstName || '',
          lastName: order.buyer.fields.lastName || '',
          email: order.buyer.fields.email || '',
        }
      }
    },
  },
}
</script>

<style scoped>
.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
.no-attendees-message {
  color: #979797;
  padding: 20px;
}
.border__left {
  border-left: 2px solid #f5f5f5;
}
.border__right {
  border-right: 2px solid #f5f5f5;
}
ul {
  list-style-type: none;
}
.err-line span {
  font-weight: bold;
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.filtering-header {
  display: flex;
  gap: 1.5rem;
  flex: 1;
}
@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .mobile-button-container {
    margin-top: 1.5rem;
  }
  .filtering-header {
    display: block;
  }
}
</style>
